import { useState, useEffect } from 'react';
import { getLanguages, setLanguage } from 'services/collections';
import { useQuery, useMutation } from '@tanstack/react-query';
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { message } from 'antd';

import { INVOICE_LANGUAGE, LANGUAGES } from 'events/types';
import { getInvoiceLang } from 'services/others';
import { queryClient } from 'config/query-client';
import { ERefetchType } from 'enums';

import useTranslation from '../../customHooks/translate';

const DefaultLanguage = () => {
  const translation = useTranslation();
  const [selectedLanguage, setSelectedLanguage] = useState<string>('');
  const [selectedLanguageName, setSelectedLanguageName] = useState<string>('');

  const { data: languages } = useQuery({
    queryKey: [LANGUAGES],
    queryFn: getLanguages,
    select: (response) => {
      return response.data.data.map((d) => ({ label: d.name, value: d.id }));
    },
  });

  const mutation = useMutation((language: string) => setLanguage(language), {
    onSuccess: () => {
      message.success(`${translation.languageSaved} ${selectedLanguageName}`);
      localStorage.setItem('selectedLanguage', selectedLanguage);
      queryClient.invalidateQueries({
        queryKey: [INVOICE_LANGUAGE],
        refetchType: ERefetchType.ALL,
      });
    },
    onError: () => {
      message.error('Failed to save the selected language');
    },
  });

  useEffect(() => {
    const fetchInvoiceLang = async () => {
      try {
        const response = await getInvoiceLang();
        const invoiceLang = response?.data;
        if (invoiceLang && languages) {
          const mappedLanguages = languages.map((lang) => ({
            label: lang.label,
            value: lang.value,
          }));
          const matchingLang = mappedLanguages.find(
            (lang) => lang.value === invoiceLang
          );
          if (matchingLang) {
            setSelectedLanguage(matchingLang.value);
            setSelectedLanguageName(matchingLang.label);
          }
        }
      } catch {}
    };

    if (languages) {
      fetchInvoiceLang();
    }
  }, [languages]);

  const handleLanguageChange = (event: SelectChangeEvent<string>) => {
    const selectedLang = event.target.value;
    const selectedLangName =
      languages?.find((lang) => lang.value === selectedLang)?.label || '';
    setSelectedLanguage(selectedLang);
    setSelectedLanguageName(selectedLangName);
  };

  const handleSave = () => {
    if (selectedLanguage) {
      mutation.mutate(selectedLanguage);
    } else {
      message.warning('Please select a language');
    }
  };

  return (
    <div className="flex w-full flex-col gap-[24px] md:w-[80%] xl:w-[50%]">
      <p className="text-[20px] font-medium text-[#1D1D1D] md:text-[24px]">
        {translation.defaultLanguage.Selectlanguage}
      </p>
      <FormControl variant="outlined">
        <InputLabel id="language-select-label">
          {translation.defaultLanguage.Language}
        </InputLabel>
        <Select
          labelId="language-select-label"
          id="language-select"
          value={selectedLanguage}
          onChange={handleLanguageChange}
          label="Language"
        >
          {languages?.map((lang) => (
            <MenuItem key={lang.value} value={lang.value}>
              {lang.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <button
        onClick={handleSave}
        className="rounded-[6px w-full rounded-[6px] bg-[#466afb] px-[24px] py-[12px] text-[14px] font-normal leading-[16px] text-white md:text-[16px]"
      >
        {translation.defaultLanguage.SaveButton}
      </button>
    </div>
  );
};

export default DefaultLanguage;
